import { AxisBottom, AxisLeft } from '@visx/axis'
import { localPoint } from '@visx/event'
import { LinearGradient } from '@visx/gradient'
import { GridRows } from '@visx/grid'
import { Group } from '@visx/group'
import { useParentSize } from '@visx/responsive'
import { scaleLinear } from '@visx/scale'
import { AreaClosed, Bar } from '@visx/shape'
import { TooltipWithBounds, defaultStyles, useTooltip } from '@visx/tooltip'
import type React from 'react'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useWorkoutMetrics } from '../../hooks/useWorkoutMetrics'
import { supabase } from '../../lib/supabase'
import { theme } from '../../theme/theme'
import type { WorkoutLap, WorkoutStep } from '../../types/database.tables'
import { formatPace, formatSwimPace, formatTimeSeconds } from '../../utils/dateUtils'
import { formatTitleCase } from '../../utils/formatting'

interface LapsChartProps {
  laps: WorkoutLap[]
  workoutSteps: WorkoutStep[]
  workoutId: string
  isCycling?: boolean
  isSwimming?: boolean
  isRunning?: boolean
}

interface AltitudeSample {
  timestamp: number
  value: number
}

const Container = styled.div`
  width: 100%;
  position: relative;
`

const TooltipActions = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.sm};
  padding-top: ${theme.spacing.sm};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`

const DeleteButton = styled.button`
  flex: 1;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  background: ${({ theme }) => theme.colors.error};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.xs};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.error || '#DC2626'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const LoadingMessage = styled.div`
  padding: ${theme.spacing.lg};
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${theme.typography.sm};
`

const MetricValue = styled.span`
  font-weight: 700;
`

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.md} 0;
  justify-content: center;
  font-size: ${theme.typography.sm};
`

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`

const LegendColorBox = styled.div<{ $color: string }>`
  width: 18px;
  height: 18px;
  background-color: ${({ $color }) => $color};
  border-radius: 3px;
`

const LegendLabel = styled.span`
  color: ${({ theme }) => theme.colors.text};
  font-size: ${theme.typography.sm};
`

const tooltipStyles = {
  ...defaultStyles,
  background: theme.colors.cardBackground,
  border: `1px solid ${theme.colors.border}`,
  borderRadius: theme.borderRadius.sm,
  padding: theme.spacing.sm,
  fontSize: theme.typography.xs,
  color: theme.colors.text,
}

const tooltipStylesWithActions = {
  ...tooltipStyles,
  pointerEvents: 'auto' as const,
}

// Helper function to determine appropriate tick interval based on duration
const getTickInterval = (durationSeconds: number): number => {
  // Convert to minutes for easier logic
  const durationMinutes = durationSeconds / 60

  if (durationMinutes <= 10) {
    return 60 // 1 minute intervals
  }
  if (durationMinutes <= 60) {
    return 300 // 5 minute intervals
  }
  if (durationMinutes <= 90) {
    return 900 // 15 minute intervals
  }
  if (durationMinutes <= 180) {
    return 1800 // 30 minute intervals
  }
  return 3600 // 1 hour intervals
}

// Helper function to generate tick values at regular intervals
const generateTicks = (maxTime: number, interval: number): number[] => {
  const ticks: number[] = [0]
  let currentTick = interval

  while (currentTick <= maxTime) {
    ticks.push(currentTick)
    currentTick += interval
  }

  // Always include the end time if it's not already there
  if (ticks[ticks.length - 1] !== maxTime && maxTime > 0) {
    ticks.push(maxTime)
  }

  return ticks
}

const formatNotes = (notes: string | undefined): string => {
  if (!notes) return ''
  // stop at first comma and return everything before
  return notes.split(',')[0]
}

// Tooltip content component
const TooltipContent = ({
  lap,
  isCycling,
  isSwimming,
  isRunning,
  isSelected,
  onDelete,
  isDeleting,
}: {
  lap: any
  isCycling: boolean
  isSwimming: boolean
  isRunning: boolean
  isSelected: boolean
  onDelete?: () => void
  isDeleting?: boolean
}) => (
  <div>
    <div style={{ fontWeight: 600, marginBottom: theme.spacing.xs }}>
      Interval {lap.lap_number + 1}
      {lap?.workoutStep?.intensity ? ` - ${formatTitleCase(lap?.workoutStep?.intensity)}` : ''}
    </div>
    {lap.workoutStep !== undefined && (
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: theme.spacing.md, marginBottom: '4px' }}>
        <span style={{ color: theme.colors.textSecondary }}>Instructions:</span>
        <MetricValue>{formatNotes(lap.workoutStep?.notes)}</MetricValue>
      </div>
    )}
    {isSwimming && lap.swim_stroke && (
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: theme.spacing.md, marginBottom: '4px' }}>
        <span style={{ color: theme.colors.textSecondary }}>Stroke:</span>
        <MetricValue>{lap.swim_stroke}</MetricValue>
      </div>
    )}
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: theme.spacing.md, marginBottom: '4px' }}>
      <span style={{ color: theme.colors.textSecondary }}>Actual Duration:</span>
      <MetricValue>{formatTimeSeconds(lap.total_elapsed_time || 0)}</MetricValue>
    </div>
    {lap.workoutStep !== undefined && (
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: theme.spacing.md, marginBottom: '4px' }}>
        <span style={{ color: theme.colors.textSecondary }}>Planned Duration:</span>
        <MetricValue>
          {lap.workoutStep?.duration_type === 'time' ? formatTimeSeconds(lap.workoutStep.duration_value) : 'Open'}
        </MetricValue>
      </div>
    )}
    {lap.total_distance !== undefined && (
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: theme.spacing.md, marginBottom: '4px' }}>
        <span style={{ color: theme.colors.textSecondary }}>Distance:</span>
        <MetricValue>
          {isSwimming ? `${lap.total_distance.toFixed(0)} m` : `${(lap.total_distance / 1000).toFixed(2)} km`}
        </MetricValue>
      </div>
    )}
    {lap.avg_power !== undefined && lap.avg_power > 0 && (
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: theme.spacing.md, marginBottom: '4px' }}>
        <span style={{ color: theme.colors.textSecondary }}>Avg Power:</span>
        <MetricValue>{lap.avg_power.toFixed(0)} W</MetricValue>
      </div>
    )}
    {(lap.workoutStep?.target_type === 'power' || lap.workoutStep?.target_type === 'power3s') &&
      lap.workoutStep?.target_value_low && (
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: theme.spacing.md, marginBottom: '4px' }}>
          <span style={{ color: theme.colors.textSecondary }}>Target Power:</span>
          <MetricValue>
            {lap.workoutStep?.target_value_low.toFixed(0)} - {lap.workoutStep?.target_value_high} W
          </MetricValue>
        </div>
      )}
    {lap.avg_speed !== undefined && (
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: theme.spacing.md, marginBottom: '4px' }}>
        <span style={{ color: theme.colors.textSecondary }}>
          {isCycling ? 'Avg Velocity:' : isSwimming ? 'Avg Pace:' : 'Avg Pace:'}
        </span>
        <MetricValue>
          {isCycling
            ? `${(lap.avg_speed * 3.6).toFixed(1)} km/h`
            : isSwimming
              ? `${formatSwimPace(lap.swimPace)} /100m`
              : `${formatPace(lap.pace)} /km`}
        </MetricValue>
      </div>
    )}
    {isCycling && lap.workoutStep?.target_type === 'speed' && lap.workoutStep?.target_value_low && (
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: theme.spacing.md, marginBottom: '4px' }}>
        <span style={{ color: theme.colors.textSecondary }}>Target Velocity:</span>
        <MetricValue>
          {lap.workoutStep?.target_value_low.toFixed(0)} - {lap.workoutStep?.target_value_high} km/h
        </MetricValue>
      </div>
    )}
    {isRunning && lap.workoutStep?.target_type === 'speed' && lap.workoutStep?.target_value_low && (
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: theme.spacing.md, marginBottom: '4px' }}>
        <span style={{ color: theme.colors.textSecondary }}>Target Pace:</span>
        <MetricValue>
          {formatPace(lap.workoutStep?.target_value_low / 600)} - {formatPace(lap.workoutStep?.target_value_high / 600)}{' '}
          min/km
        </MetricValue>
      </div>
    )}
    {lap.avg_heart_rate !== undefined && (
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: theme.spacing.md, marginBottom: '4px' }}>
        <span style={{ color: theme.colors.textSecondary }}>Avg HR:</span>
        <MetricValue>{lap.avg_heart_rate} bpm</MetricValue>
      </div>
    )}
    {lap.workoutStep?.target_type === 'heart_rate' && lap.workoutStep?.target_value_low && (
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: theme.spacing.md, marginBottom: '4px' }}>
        <span style={{ color: theme.colors.textSecondary }}>Target Heart Rate:</span>
        <MetricValue>
          {lap.workoutStep?.target_value_low.toFixed(0)} - {lap.workoutStep?.target_value_high} bpm
        </MetricValue>
      </div>
    )}
    {isSelected && onDelete && (
      <TooltipActions>
        <DeleteButton onClick={onDelete} disabled={isDeleting}>
          {isDeleting ? 'Deleting...' : 'Delete'}
        </DeleteButton>
      </TooltipActions>
    )}
  </div>
)

// Helper function to get bar color based on swim stroke (for swimming) or intensity (for other activities)
const getBarColor = (lap: Omit<WorkoutLap, 'start_time'>, isSwimming?: boolean): string => {
  const intensity = lap.intensity
  const swimStroke = lap.swim_stroke

  // TODO: either compare with intervals or improve lap matching
  // laps do also contain manual laps, which do not match with workout steps
  // console.log('lap+step', lap, step)
  // if (step?.duration_value && step?.duration_value !== lap.total_elapsed_time) {
  //  return '#ed7474'
  //}

  // For swimming, use stroke-specific colors
  if (isSwimming && swimStroke) {
    switch (swimStroke.toLowerCase()) {
      case 'freestyle':
        return '#3B82F6' // Blue
      case 'backstroke':
        return '#8B5CF6' // Purple
      case 'breaststroke':
        return '#10B981' // Green
      case 'butterfly':
        return '#F59E0B' // Amber/Orange
      case 'mixed':
        return '#EC4899' // Pink
      case 'drill':
        return '#6366F1' // Indigo
      default:
        return '#06B6D4' // Cyan (default swimming color)
    }
  }

  // For non-swimming activities, use intensity-based colors
  switch (intensity) {
    case 'active':
      return '#10B981' // Green
    case 'rest':
      return '#6B7280' // Gray
    case 'warmup':
      return '#a5cfc1' // Light teal
    case 'cooldown':
      return '#a5cfc1' // Light teal
    default:
      return 'rgba(113,204,173,0.75)' // Light gray
  }
}

export const LapsChart: React.FC<LapsChartProps> = ({
  laps,
  workoutId,
  workoutSteps,
  isCycling = false,
  isSwimming = false,
  isRunning = false,
}) => {
  const { tooltipData, tooltipLeft, tooltipTop, tooltipOpen, showTooltip, hideTooltip } = useTooltip<any>()
  const { parentRef, width } = useParentSize({ debounceTime: 150 })
  const [altitudeData, setAltitudeData] = useState<{ timestamp: number; value: number }[]>([])
  const [selectedLapId, setSelectedLapId] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const { data } = useWorkoutMetrics(workoutId, 'altitude', { downsampleInterval: 5, smoothingWindow: 5 })

  // Fetch altitude data
  useEffect(() => {
    const loadAltitude = async () => {
      if (!data) return
      const samples = (data.samples as AltitudeSample[]).map((sample) => ({
        timestamp: sample.timestamp,
        value: sample.value,
      }))
      setAltitudeData(samples)
    }

    loadAltitude()
  }, [data])

  // Handle lap deletion
  const handleDeleteLap = async () => {
    if (!selectedLapId) return

    try {
      setIsDeleting(true)
      const { error } = await supabase.from('workout_laps').delete().eq('id', selectedLapId)

      if (error) {
        console.error('Error deleting lap:', error)
        alert('Failed to delete lap. Please try again.')
        return
      }

      // Reset selection and trigger parent component to reload laps
      setSelectedLapId(null)

      // Remove the deleted lap from the local state
      laps.splice(
        laps.findIndex((lap) => lap.id === selectedLapId),
        1
      )
    } catch (err) {
      console.error('Error deleting lap:', err)
      alert('Failed to delete lap. Please try again.')
    } finally {
      setIsDeleting(false)
    }
  }

  // Handle lap selection
  const handleLapClick = (lapId: string, lapData: any, event: any) => {
    if (selectedLapId === lapId) {
      // Deselect if clicking the same lap
      setSelectedLapId(null)
      hideTooltip()
    } else {
      setSelectedLapId(lapId)
      // Keep tooltip visible at current position
      const point = localPoint(event)
      if (point) {
        showTooltip({
          tooltipData: lapData,
          tooltipLeft: point.x,
          tooltipTop: point.y,
        })
      }
    }
  }

  if (!laps || laps.length === 0) {
    return (
      <Container>
        <LoadingMessage>No lap data available.</LoadingMessage>
      </Container>
    )
  }

  // Chart dimensions
  const height = 250
  const margin = { top: 20, right: 20, bottom: 40, left: 60 }
  const innerWidth = width - margin.left - margin.right
  const innerHeight = height - margin.top - margin.bottom

  // Prepare chart data
  const totalDuration = laps.reduce((sum, lap) => sum + (lap.total_elapsed_time || 0), 0)
  let cumulativeTime = 0

  const chartData = laps.map((lap, _index) => {
    // Calculate pace in min/km for running (inverse of speed)
    const avgSpeedKmh = (lap.avg_speed || 0) * 3.6
    const paceMinPerKm = avgSpeedKmh > 0 ? 60 / avgSpeedKmh : 0

    // Calculate pace in min/100m for swimming
    // avg_speed is in m/s, we need min/100m
    const avgSpeedMps = lap.avg_speed || 0
    const swimPaceMinPer100m = avgSpeedMps > 0 ? 100 / avgSpeedMps / 60 : 0

    const lapDuration = lap.total_elapsed_time || 0
    const startTime = cumulativeTime
    const endTime = cumulativeTime + lapDuration
    cumulativeTime = endTime

    const workoutStep = workoutSteps.find((step) => step.step_index === lap.workout_step_index)

    return {
      ...lap,
      workoutStep,
      /* */
      avg_power: lap.avg_power || 0,
      avg_speed: lap.avg_speed || 0,

      avg_heart_rate: lap.avg_heart_rate || 0,
      total_distance: lap.total_distance || 0,
      total_calories: lap.total_calories || 0,
      intensity: lap.intensity,
      swim_stroke: lap.swim_stroke,
      id: lap.id,
      /* */
      total_elapsed_time: lapDuration,
      start_time: startTime,
      end_time: endTime,
      pace: paceMinPerKm,
      swimPace: swimPaceMinPer100m,
    }
  })

  // Scales
  const xScale = scaleLinear({
    domain: [0, totalDuration],
    range: [0, innerWidth],
  })

  // Calculate max metric value only from non-rest laps
  const maxMetricValue = Math.max(
    ...chartData
      .filter((d) => d.intensity !== 'rest')
      .map((d) => (isCycling ? (d.avg_power ?? 0) : isSwimming ? d.swimPace : d.pace))
  )

  // Calculate sensible max Y value based on activity type
  let maxYValue: number
  let minYValue = 0

  if (isCycling) {
    // For cycling: round up to nearest 10W
    maxYValue = Math.ceil(maxMetricValue / 10) * 10
  } else if (isSwimming) {
    // For swimming: use the slowest pace and add some padding (e.g., add 0.5 min/100m)
    // Also set a sensible minimum (fastest pace) based on the data
    const minPace = Math.min(
      ...chartData
        .filter((d) => d.intensity !== 'rest')
        .map((d) => d.swimPace)
        .filter((p) => p > 0)
    )
    const maxPace = maxMetricValue

    // Add 10% padding above slowest pace, but at least 0.2 min (12 seconds)
    const padding = Math.max((maxPace - minPace) * 0.1, 0.2)
    minYValue = Math.max(0, minPace - padding)
    maxYValue = maxPace + padding
  } else {
    // For running: similar approach - add padding to pace range
    const minPace = Math.min(
      ...chartData
        .filter((d) => d.intensity !== 'rest')
        .map((d) => d.pace)
        .filter((p) => p > 0)
    )
    const maxPace = maxMetricValue

    const padding = Math.max((maxPace - minPace) * 0.1, 0.2)
    minYValue = Math.max(0, minPace - padding)
    maxYValue = maxPace + padding
  }

  // For pace (running/swimming), invert the domain so faster pace (lower values) appears higher
  // This makes the chart more intuitive: taller bars = faster pace
  const yScale = scaleLinear({
    domain: isCycling ? [0, maxYValue] : [maxYValue, minYValue],
    range: [innerHeight, 0],
  })

  // Calculate appropriate tick interval and generate tick values for X-axis
  const tickInterval = getTickInterval(totalDuration)
  const xAxisTicks = generateTicks(totalDuration, tickInterval)

  // Altitude scale (for background area)
  let altitudeScale: any = null
  if (altitudeData.length > 0) {
    const altitudeValues = altitudeData.map((d) => d.value)
    const minAltitude = Math.min(...altitudeValues)
    const maxAltitude = Math.max(...altitudeValues)
    const altitudePadding = (maxAltitude - minAltitude) * 0.1

    altitudeScale = scaleLinear({
      domain: [minAltitude - altitudePadding, maxAltitude + altitudePadding],
      range: [innerHeight, 0],
    })
  }

  // Gap between bars for visual separation (only for consecutive non-rest laps)
  const gapSize = 2

  // Swimming stroke legend data - all possible strokes
  const allStrokeLegend = [
    { stroke: 'freestyle', label: 'Freestyle', color: '#3B82F6' },
    { stroke: 'backstroke', label: 'Backstroke', color: '#8B5CF6' },
    { stroke: 'breaststroke', label: 'Breaststroke', color: '#10B981' },
    { stroke: 'butterfly', label: 'Butterfly', color: '#F59E0B' },
    { stroke: 'mixed', label: 'Mixed', color: '#EC4899' },
    { stroke: 'drill', label: 'Drill', color: '#6366F1' },
  ]

  // Get unique stroke types present in the data
  const strokesInData = new Set(
    chartData.filter((lap) => lap.swim_stroke && lap.intensity !== 'rest').map((lap) => lap.swim_stroke?.toLowerCase())
  )

  // Filter legend to only show strokes present in the data
  const strokeLegend = allStrokeLegend.filter((item) => strokesInData.has(item.stroke))

  // Find selected lap data
  const selectedLap = selectedLapId ? chartData.find((lap) => lap.id === selectedLapId) : null

  return (
    <Container ref={parentRef}>
      {/** biome-ignore lint/a11y/noSvgWithoutTitle: title conflicts with tooltip */}
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`}>
        {/* Altitude gradient definition */}
        {altitudeData.length > 0 && (
          <LinearGradient
            id="altitude-gradient"
            from="#9CA3AF"
            to="#9CA3AF"
            fromOpacity={0.5}
            toOpacity={0.2}
            vertical
          />
        )}

        <Group left={margin.left} top={margin.top}>
          {/* Background click area to deselect */}
          <rect
            x={0}
            y={0}
            width={innerWidth}
            height={innerHeight}
            fill="transparent"
            onKeyPress={(event) => event.key === 'Enter' && setSelectedLapId(null)}
            onClick={() => {
              if (selectedLapId) {
                setSelectedLapId(null)
                hideTooltip()
              }
            }}
            style={{ cursor: selectedLapId ? 'pointer' : 'default' }}
          />

          {/* Altitude area in background */}
          {altitudeData.length > 0 && altitudeScale && (
            <AreaClosed
              data={altitudeData}
              x={(d) => xScale(d.timestamp)}
              y={(d) => altitudeScale(d.value)}
              yScale={altitudeScale}
              fill="url(#altitude-gradient)"
              stroke="#9CA3AF"
              strokeWidth={0}
            />
          )}

          {/* Grid */}
          <GridRows scale={yScale} width={innerWidth} stroke="#E5E7EB" strokeOpacity={0.5} />

          {/* Bars - swimming: only render non-rest laps , rest laps appear as gaps */}
          {chartData.map((lap) => {
            // Skip rest laps - they will appear as empty space
            if (isSwimming && lap.intensity === 'rest') {
              return null
            }

            const barX = xScale(lap.start_time) + gapSize
            const barWidth = Math.max(xScale(lap.end_time) - xScale(lap.start_time) - gapSize * 2, 1)
            const metricValue = isCycling ? (lap.avg_power ?? 0) : isSwimming ? (lap.swimPace ?? 0) : (lap.pace ?? 0)
            const barHeight = innerHeight - yScale(metricValue)
            const barY = yScale(metricValue)
            const isSelected = selectedLapId === lap.id

            return (
              <g key={lap.id}>
                <Bar
                  x={barX}
                  y={barY}
                  width={barWidth}
                  height={barHeight}
                  fill={`${getBarColor(lap, isSwimming)}CC`}
                  rx={4}
                  style={{ cursor: 'pointer' }}
                  onMouseMove={(event) => {
                    // Don't change tooltip if a lap is already selected
                    if (!selectedLapId) {
                      const point = localPoint(event)
                      showTooltip({
                        tooltipData: lap,
                        tooltipLeft: point?.x,
                        tooltipTop: point?.y,
                      })
                    }
                  }}
                  onMouseLeave={() => {
                    // Don't hide tooltip if this lap is selected
                    if (selectedLapId !== lap.id) {
                      hideTooltip()
                    }
                  }}
                  onClick={(event) => {
                    event.stopPropagation()
                    handleLapClick(lap.id, lap, event)
                  }}
                />
                {/* Selection indicator */}
                {isSelected && (
                  <rect
                    x={barX - 2}
                    y={barY - 2}
                    width={barWidth + 4}
                    height={barHeight + 4}
                    fill="none"
                    stroke="#FFC30D"
                    strokeWidth={3}
                    rx={5}
                    pointerEvents="none"
                  />
                )}
              </g>
            )
          })}

          {/* Axes */}
          <AxisBottom
            top={innerHeight}
            scale={xScale}
            stroke={theme.colors.textSecondary}
            tickStroke={theme.colors.textSecondary}
            tickValues={xAxisTicks}
            tickFormat={(value) => formatTimeSeconds(Number(value))}
            tickLabelProps={() => ({
              fill: theme.colors.textSecondary,
              fontSize: 10,
              textAnchor: 'middle',
            })}
            label="Time"
            labelProps={{
              fill: theme.colors.text,
              fontSize: 12,
              textAnchor: 'middle',
            }}
          />
          <AxisLeft
            scale={yScale}
            stroke={theme.colors.textSecondary}
            tickStroke={theme.colors.textSecondary}
            tickFormat={(value) =>
              isCycling ? String(value) : isSwimming ? formatSwimPace(Number(value)) : formatPace(Number(value))
            }
            tickLabelProps={() => ({
              fill: theme.colors.textSecondary,
              fontSize: 10,
              textAnchor: 'end',
              dx: -4,
            })}
            label={isCycling ? 'Power (W)' : isSwimming ? 'Pace (min/100m)' : 'Pace (min/km)'}
            labelProps={{
              fill: theme.colors.text,
              fontSize: 12,
              textAnchor: 'middle',
            }}
          />
        </Group>
      </svg>

      {/* Tooltip - show when hovering OR when a lap is selected */}
      {((tooltipOpen && tooltipData) || selectedLap) && (
        <TooltipWithBounds
          top={selectedLap ? tooltipTop || 100 : tooltipTop}
          left={selectedLap ? tooltipLeft || width / 2 : tooltipLeft}
          style={selectedLap ? tooltipStylesWithActions : tooltipStyles}
        >
          <TooltipContent
            lap={selectedLap || tooltipData}
            isCycling={isCycling}
            isSwimming={isSwimming}
            isRunning={isRunning}
            isSelected={!!selectedLap}
            onDelete={selectedLap ? handleDeleteLap : undefined}
            isDeleting={isDeleting}
          />
        </TooltipWithBounds>
      )}

      {/* Swimming stroke legend */}
      {isSwimming && strokeLegend.length > 0 && (
        <Legend>
          {strokeLegend.map((item) => (
            <LegendItem key={item.stroke}>
              <LegendColorBox $color={item.color} />
              <LegendLabel>{item.label}</LegendLabel>
            </LegendItem>
          ))}
        </Legend>
      )}
    </Container>
  )
}
